/**
 * The corpus asset-liveness facts, for consumers that must run synchronously.
 *
 * `builder/data/asset-liveness.json` is produced by
 * `scripts/parsers/asset-liveness-sweep.js`. It records the image URLs that are
 * BOTH on a dead source domain AND unsatisfiable from the owning reference
 * bundle — i.e. genuinely unrecoverable for that template pair.
 *
 * WHY EXACT URLS AND NOT JUST HOSTS
 * ---------------------------------
 * A dead origin does not condemn a url. 1,576 images were recovered from the
 * Wayback Machine and vendored under their ORIGINAL urls, so they resolve from
 * disk with the origin still dead. Escalating on host alone would refuse exports
 * over assets that work — a false block, which is worse than the CLEANUP-severity
 * status quo it replaces.
 *
 * WHY A MODULE CACHE RATHER THAN CTX THREADING
 * --------------------------------------------
 * `runAudit` is called from five places. Requiring each to inject this would
 * mean any missed call site SILENTLY stops escalating — a defect with no symptom.
 * `audit-engine.js` reads it once in `defaultCtx()` instead, so every caller gets
 * it and none can forget.
 *
 * FAIL-SOFT, DELIBERATELY. If the file is absent or unloaded, the set is empty
 * and the audit behaves exactly as it did before this existed (CLEANUP). This is
 * an enhancement to a report, not a security boundary: failing closed would block
 * every export whenever a data file is missing.
 */

let deadUrls = new Set();
let deadPairs = new Map();
let deadHosts = new Set();
let pairAware = false;
let loaded = false;
let loading = null;

/** Data key, resolved through the asset loader / R2 manifest like any other. */
export const ASSET_LIVENESS_KEY = 'data/asset-liveness.json';

function normalizeHost(host) {
  let value = String(host || '')
    .trim()
    .toLowerCase();
  try {
    if (/^https?:\/\//.test(value)) value = new URL(value).host;
  } catch {
    return '';
  }
  return value.replace(/^www\./, '').split('/')[0];
}

function addUrlVariants(target, url) {
  target.add(url);
  try {
    const bare = new URL(url);
    bare.host = normalizeHost(bare.host);
    target.add(bare.href);
    const wwwed = new URL(url);
    if (!wwwed.host.startsWith('www.')) {
      wwwed.host = `www.${wwwed.host}`;
      target.add(wwwed.href);
    }
  } catch {
    /* unparseable entry — the literal form is already recorded */
  }
}

/** Populate from an already-parsed artifact. Pure; used directly by tests. */
export function setAssetLivenessData(data) {
  const urls = new Set();
  for (const url of (data && data.unresolvedUrls) || []) {
    addUrlVariants(urls, url);
  }
  const pairs = new Map();
  for (const [domain, pairUrls] of Object.entries(data?.unresolvedPairs || {})) {
    const key = normalizeHost(domain);
    if (!key) continue;
    const values = new Set();
    for (const url of pairUrls || []) addUrlVariants(values, url);
    pairs.set(key, values);
  }
  deadUrls = urls;
  deadPairs = pairs;
  pairAware = Object.prototype.hasOwnProperty.call(data || {}, 'unresolvedPairs');
  deadHosts = new Set(((data && data.deadHosts) || []).map(normalizeHost));
  loaded = true;
}

/** Reset to the unloaded state (tests only). */
export function resetAssetLivenessData() {
  deadUrls = new Set();
  deadPairs = new Map();
  deadHosts = new Set();
  pairAware = false;
  loaded = false;
  loading = null;
}

/**
 * Load the artifact once. Safe to call repeatedly and from several places; the
 * in-flight promise is shared. A missing file is NOT an error — see fail-soft.
 *
 * @param {(key: string) => Promise<object>} loadJSON - injected loader
 */
export function loadAssetLivenessData(loadJSON) {
  if (loaded) return Promise.resolve(true);
  if (loading) return loading;
  loading = (async () => {
    try {
      setAssetLivenessData(await loadJSON(ASSET_LIVENESS_KEY));
      return true;
    } catch {
      // Expected flow: the corpus may ship without a sweep having run.
      loaded = true;
      return false;
    } finally {
      loading = null;
    }
  })();
  return loading;
}

/** True once a load attempt has completed (successfully or not). */
export function assetLivenessLoaded() {
  return loaded;
}

/**
 * Is this URL known to be unrecoverable for the owning bundle? False whenever
 * the data is absent, which preserves pre-existing behaviour. A caller without
 * source-domain context receives the conservative legacy/global answer.
 */
export function isKnownDeadAssetUrl(url, sourceDomain = '') {
  const value = String(url || '');
  const domain = normalizeHost(sourceDomain);
  if (pairAware && domain) return deadPairs.get(domain)?.has(value) || false;
  return deadUrls.has(value);
}

/** Hosts with at least one unrecoverable url. Informational only. */
export function knownDeadAssetHosts() {
  return deadHosts;
}

/** The snapshot handed to audit checks through `ctx`. */
export function assetLivenessCtx() {
  return {
    isKnownDeadAssetUrl,
    assetLivenessLoaded: loaded
  };
}
